import { css, cx } from "@emotion/css";
import { graphql, useFragment } from "react-relay";
import { useAccount } from "wagmi";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

import * as theme from "../../theme";
import { HStack, VStack } from "../../components/VStack";
import { Markdown } from "../../components/Markdown";
import { NounResolvedLink } from "../../components/NounResolvedLink";
import { ProposalTransactionDisplay } from "../../components/ProposalTransactionDisplay";
import { icons } from "../../icons/icons";
import { useOpenDialog } from "../../components/DialogProvider/DialogProvider";
import { contracts } from "../../utils/contracts";
import { useContractWrite } from "../../hooks/useContractWrite";
import TransactionStatusDialog from "../../components/TransactionStatusDialog";
import { getEtherscanLink } from "../../utils/getEtherscanLink";
import { getLinkToNounsWtf } from "../../utils/getLinkToNounsWtf";
import TimestampTooltip from "../../components/TimestampTooltip";

import { ProposalDetailPanelFragment$key } from "./__generated__/ProposalDetailPanelFragment.graphql";

export function ProposalDetailPanel({
  fragmentRef,
}: {
  fragmentRef: ProposalDetailPanelFragment$key;
}) {
  const openDialog = useOpenDialog();

  const proposal = useFragment(
    graphql`
      fragment ProposalDetailPanelFragment on OnChainProposal {
        number
        title
        description
        status

        updatePeriodEndsAt
        objectionPeriodEndsAt
        voteEndsAt
        voteStartsAt

        # eslint-disable-next-line relay/unused-fields
        transactions {
          signature
          calldata
          ...ProposalTransactionDisplayFragment
        }

        proposer {
          address {
            resolvedName {
              ...NounResolvedLinkFragment
              address
            }
          }
        }

        signers {
          address {
            resolvedName {
              ...NounResolvedLinkFragment
              address
            }
          }
        }

        updateHistory {
          timestamp
        }

        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...UpdateHistoryDialogContentFragment
      }
    `,
    fragmentRef
  );

  const lastUpdate = proposal.updateHistory[0]?.timestamp;

  const account = useAccount();

  const isAbleToCancel =
    account.address === proposal.proposer.address.resolvedName.address ||
    proposal.signers.some(
      (signer) => signer.address.resolvedName.address === account.address
    );

  const isAbleToEdit =
    proposal.status === "UPDATABLE" &&
    account.address === proposal.proposer.address.resolvedName.address;

  return (
    <>
      <VStack
        gap="4"
        className={css`
          width: 100%;
        `}
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          className={css`
            @media (max-width: ${theme.maxWidth["2xl"]}) {
              flex-direction: column-reverse;
              align-items: flex-start;
            }
          `}
        >
          <VStack>
            <div
              className={css`
                font-size: ${theme.fontSize.xs};
                font-weight: ${theme.fontWeight.semibold};
                color: ${theme.colors.gray[700]};
              `}
            >
              <HStack gap="1">
                Proposal {proposal.number} by
                <NounResolvedLink
                  resolvedName={proposal.proposer.address.resolvedName}
                />
                {proposal.signers.length > 0 && (
                  <HStack gap="0">
                    (sponsored by &nbsp;
                    {proposal.signers.map((signer, idx) => (
                      <>
                        <NounResolvedLink
                          resolvedName={signer.address.resolvedName}
                        />
                        {idx === proposal.signers.length - 1 ? "" : ", "}
                      </>
                    ))}
                    )
                  </HStack>
                )}
                {lastUpdate && proposal.updateHistory.length > 1 ? (
                  <div
                    className={css`
                      cursor: pointer;
                    `}
                    onClick={() => {
                      openDialog({
                        type: "SHOW_UPDATE_HISTORY",
                        params: {
                          fragmentRef: proposal,
                        },
                      });
                    }}
                  >
                    {" "}
                    <TimestampTooltip date={lastUpdate}>
                      - updated {formatDistanceToNow(new Date(lastUpdate))} ago
                    </TimestampTooltip>
                  </div>
                ) : (
                  <>
                    {" "}
                    <TimestampTooltip date={lastUpdate}>
                      - created {formatDistanceToNow(new Date(lastUpdate))} ago
                    </TimestampTooltip>
                  </>
                )}
                {proposal.updateHistory.length > 1 && (
                  <img
                    className={css`
                      width: 12px;
                      height: 12px;
                      align-self: center;
                      margin-top: 2px;
                      cursor: pointer;
                    `}
                    onClick={() => {
                      openDialog({
                        type: "SHOW_UPDATE_HISTORY",
                        params: {
                          fragmentRef: proposal,
                        },
                      });
                    }}
                    src={icons.info}
                  />
                )}
              </HStack>
            </div>
            <h2
              className={css`
                font-size: ${theme.fontSize["2xl"]};
                font-weight: ${theme.fontWeight.black};
              `}
            >
              {proposal.title}
            </h2>
          </VStack>
          <VStack
            className={css`
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              @media (max-width: ${theme.maxWidth["2xl"]}) {
                display: none;
              }
              white-space: nowrap;
            `}
          >
            <div
              className={css`
                font-size: ${theme.fontSize.xs};
                font-weight: ${theme.fontWeight.semibold};
                color: ${theme.colors.gray[700]};
              `}
            >
              <HStack gap="1">
                {(() => {
                  switch (proposal.status) {
                    case "CANCELLED":
                      return "Cancelled";

                    case "OBJECTION_PERIOD":
                      return "Objection period";

                    default:
                      return "Voting";
                  }
                })()}
              </HStack>
            </div>
            <h4
              className={css`
                font-size: ${theme.fontSize["base"]};
                font-weight: ${theme.fontWeight.normal};
              `}
            >
              {(() => {
                switch (proposal.status) {
                  case "CANCELLED":
                    return (
                      <TimestampTooltip date={proposal.voteEndsAt}>
                        {formatDistanceToNow(new Date(proposal.voteEndsAt))} ago
                      </TimestampTooltip>
                    );

                  case "UPDATABLE":
                  case "PENDING":
                    return (
                      <HStack gap="1">
                        Starts in{" "}
                        {
                          <TimestampTooltip date={proposal.voteStartsAt}>
                            {formatDistanceToNow(
                              new Date(proposal.voteStartsAt)
                            )}
                          </TimestampTooltip>
                        }
                      </HStack>
                    );

                  case "ACTIVE":
                    return (
                      <HStack gap="1">
                        Ends in{" "}
                        {
                          <TimestampTooltip date={proposal.voteEndsAt}>
                            {formatDistanceToNow(new Date(proposal.voteEndsAt))}
                          </TimestampTooltip>
                        }
                      </HStack>
                    );

                  case "OBJECTION_PERIOD":
                    return (
                      <HStack gap="1">
                        Ends in{" "}
                        {
                          <TimestampTooltip
                            date={proposal.objectionPeriodEndsAt}
                          >
                            {formatDistanceToNow(
                              new Date(proposal.objectionPeriodEndsAt)
                            )}
                          </TimestampTooltip>
                        }
                      </HStack>
                    );

                  default:
                    return (
                      <HStack gap="1">
                        Ended{" "}
                        {
                          <TimestampTooltip date={proposal.voteEndsAt}>
                            {formatDistanceToNow(new Date(proposal.voteEndsAt))}{" "}
                            ago
                          </TimestampTooltip>
                        }
                      </HStack>
                    );
                }
              })()}
            </h4>
          </VStack>
        </HStack>
        <VStack
          gap="2"
          className={css`
            margin-bottom: ${theme.spacing["16"]};
            @media (max-width: ${theme.maxWidth["2xl"]}) {
              margin-bottom: ${theme.spacing["48"]};
            }
          `}
        >
          {isAbleToCancel &&
            (proposal.status === "UPDATABLE" ||
              proposal.status === "PENDING" ||
              proposal.status === "ACTIVE" ||
              proposal.status === "OBJECTION_PERIOD") && (
              <ProposerActionPanel
                canEdit={isAbleToEdit}
                updatePeriodEndsAt={proposal.updatePeriodEndsAt}
                proposalId={proposal.number}
              />
            )}
          <VStack
            gap="1"
            className={css`
              border: 1px solid #e0e0e0;
              border-radius: ${theme.borderRadius.lg};
              background-color: ${theme.colors.gray["fa"]};
            `}
          >
            <VStack
              className={css`
                padding: ${theme.spacing["4"]} ${theme.spacing["4"]} 0
                  ${theme.spacing["4"]};
              `}
            >
              <div
                className={css`
                  font-size: ${theme.fontSize.xs};
                  font-family: ${theme.fontFamily.mono};
                  font-weight: ${theme.fontWeight.medium};
                  color: ${theme.colors.gray.af};
                  line-height: ${theme.lineHeight["4"]};
                  margin-bottom: ${theme.spacing[2]};
                `}
              >
                Proposed Transactions
              </div>
              <VStack>
                {proposal.transactions.map((tx, idx) => (
                  <ProposalTransactionDisplay
                    key={idx}
                    fragment={tx}
                    transactions={proposal.transactions}
                  />
                ))}
              </VStack>
            </VStack>
          </VStack>
          <Markdown
            markdown={stripTitleFromDescription(
              proposal.title,
              proposal.description
            )}
          />
        </VStack>
      </VStack>
    </>
  );
}

function ProposerActionPanel({
  canEdit,
  updatePeriodEndsAt,
  proposalId,
}: {
  canEdit: boolean;
  updatePeriodEndsAt: number;
  proposalId: string;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { write, isLoading, isError, isSuccess, txHash } = useContractWrite(
    contracts.nounsDao,
    "cancel",
    [proposalId],
    () => {}
  );

  return (
    <>
      <TransactionStatusDialog
        messages={{
          waiting: "Waiting for transaction submission",
          loading: "Canceling proposal",
          success: {
            title: "Proposal canceled",
            link: getEtherscanLink(txHash),
          },
          failure: "Error while canceling proposal",
        }}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
      />
      <VStack
        gap="1"
        className={css`
          margin-bottom: ${theme.spacing["4"]};
          border: 1px solid #e0e0e0;
          border-radius: ${theme.borderRadius.lg};
          background-color: ${theme.colors.white};
        `}
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          className={css`
            padding: ${theme.spacing["4"]} ${theme.spacing["4"]}
              ${theme.spacing["4"]} ${theme.spacing["4"]};
          `}
        >
          <div
            className={css`
              font-size: ${theme.fontSize.xs};
              font-weight: ${theme.fontWeight.semibold};
              color: ${theme.colors.gray[700]};
              max-width: 400px;
            `}
          >
            {canEdit
              ? `You have ${formatDistanceToNow(
                  new Date(updatePeriodEndsAt)
                )} left to edit this prop. After this period, only cancel will be available`
              : `Updatable period has ended. But you can cancel this proposal.`}
          </div>
          <HStack gap="3">
            <CancelButton
              title="Cancel"
              className={css`
                color: ${theme.colors.red["600"]};
              `}
              onClick={() => {
                write();
                setIsDialogOpen(true);
              }}
            />
            {canEdit && (
              <ActionButtonLink
                title="Edit prop"
                link={getLinkToNounsWtf(`vote/${proposalId}`)}
                className={css`
                  color: ${theme.colors.black};
                `}
              />
            )}
          </HStack>
        </HStack>
      </VStack>
    </>
  );
}

function CancelButton({
  title,
  className,
  onClick,
}: {
  title: string;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cx(
        css`
          text-align: center;
          border-radius: ${theme.spacing["2"]};
          border: 1px solid ${theme.colors.gray.eb};
          font-weight: ${theme.fontWeight.semibold};
          font-size: ${theme.fontSize.xs};
          padding: ${theme.spacing["2"]} ${theme.spacing["6"]};
          cursor: pointer;

          :hover {
            background: ${theme.colors.gray.eb};
          }
        `,
        className
      )}
    >
      {title}
    </div>
  );
}

function ActionButtonLink({
  title,
  link,
  className,
}: {
  title: string;
  link: string;
  className?: string;
}) {
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={cx(
        css`
          text-align: center;
          border-radius: ${theme.spacing["2"]};
          border: 1px solid ${theme.colors.gray.eb};
          font-weight: ${theme.fontWeight.semibold};
          font-size: ${theme.fontSize.xs};
          padding: ${theme.spacing["2"]} ${theme.spacing["6"]};
          cursor: pointer;

          :hover {
            background: ${theme.colors.gray.eb};
          }
        `,
        className
      )}
    >
      {title}
    </a>
  );
}

function stripTitleFromDescription(title: string, description: string) {
  // TODO: This is very fragile. Consider using a regex instead?
  if (description.startsWith(`# ${title}\n`)) {
    const newDescription = description.slice(`# ${title}\n`.length).trim();
    return newDescription;
  }
  return description;
}
